import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { currencyFormatter, dateFormatter } from "../constants";

export type RecentTransaction = {
    id: number;
    credit: number;
    debit: number;
    balance?: number;
    createdAt: string;
    name?: string;
    description?: string;
    status?: string;
    client?: { employee?: { name?: string } };
    bank?: { name?: string };
};

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
    },
    header: {
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    headerText: {
        flex: 1,
        minWidth: 200,
    },
    tableHead: {
        background: theme.palette.common.black,
        "& th": {
            color: theme.palette.common.white,
            fontWeight: 700,
            fontSize: "0.8rem",
            whiteSpace: "nowrap",
        },
    },
    row: {
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    empty: {
        padding: theme.spacing(4),
        textAlign: "center",
        color: theme.palette.text.secondary,
    },
    amountDebit: {
        color: "#2e7d32",
        fontWeight: 600,
    },
    amountCredit: {
        color: "#e65100",
        fontWeight: 600,
    },
    muted: {
        color: theme.palette.text.secondary,
    },
}));

type Props = {
    transactions: RecentTransaction[];
};

const RecentTransactions = ({ transactions }: Props) => {
    const classes = useStyles();
    const history = useHistory();

    return (
        <Paper className={classes.paper} elevation={0}>
            <Box className={classes.header}>
                <Box className={classes.headerText}>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Recent Transactions
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                        Latest {transactions.length} entries from transaction
                        summary
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => history.push("/transactions")}
                >
                    View Overall Summary
                </Button>
            </Box>

            {transactions.length === 0 ? (
                <Typography className={classes.empty}>
                    No recent transactions found
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>Transaction</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Credit</TableCell>
                                <TableCell align="right">Debit</TableCell>
                                <TableCell>Bank</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map((row) => {
                                const debit = Number(row.debit) || 0;
                                const credit = Number(row.credit) || 0;
                                const isDebit = debit > 0 && credit === 0;
                                const isCredit = credit > 0 && debit === 0;

                                return (
                                    <TableRow
                                        key={row.id}
                                        className={classes.row}
                                    >
                                        <TableCell
                                            style={{ whiteSpace: "nowrap" }}
                                        >
                                            TRA-{row.id}
                                        </TableCell>
                                        <TableCell
                                            style={{ whiteSpace: "nowrap" }}
                                        >
                                            {dateFormatter.format(
                                                new Date(row.createdAt)
                                            )}
                                        </TableCell>
                                        <TableCell
                                            style={{ whiteSpace: "nowrap" }}
                                        >
                                            {row.client?.employee?.name || "—"}
                                        </TableCell>
                                        <TableCell>
                                            {isDebit ? (
                                                <Chip
                                                    size="small"
                                                    label="Debit"
                                                    style={{
                                                        background:
                                                            "linear-gradient(45deg, #7F0900, #FF1200)",
                                                        color: "#fff",
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            ) : isCredit ? (
                                                <Chip
                                                    size="small"
                                                    label="Credit"
                                                    style={{
                                                        background:
                                                            "linear-gradient(45deg, #45BF55, #00261C)",
                                                        color: "#fff",
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={
                                                        row.status || "Other"
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.amountCredit}
                                        >
                                            {credit > 0
                                                ? currencyFormatter(credit)
                                                : "—"}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.amountDebit}
                                        >
                                            {debit > 0
                                                ? currencyFormatter(debit)
                                                : "—"}
                                        </TableCell>
                                        <TableCell>
                                            {row.bank?.name || "—"}
                                        </TableCell>
                                        <TableCell>
                                            {row.description ||
                                                row.name || (
                                                    <span
                                                        className={
                                                            classes.muted
                                                        }
                                                    >
                                                        N/A
                                                    </span>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default RecentTransactions;
